import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import patientService from '../../../../../services/patient.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../../utils/ecxSwal';
import PatientDetailIcon from '../subparts/PatientDetailIcon';
import PatientAppointments from './PatientAppointments';
import PatientActivity from './PatientActivity';
import PatientInvalidDetails from './PatientInvalidData';

const stripPhoneFormatting = (value) => (value ? value.replace(/\D/g, '') : value);

const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) {
        return null;
    }

    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
    }

    return age;
};

function DetailField({ label, value, children }) {
    return (
        <div className="patient-detail-field">
            <span className="patient-detail-field__label">{label}</span>
            {children || <div className="patient-detail-field__value">{value || '—'}</div>}
        </div>
    );
}

function Overview({ patient, onPatientUpdated }) {
    const [isEditing, setIsEditing] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const {
        register,
        handleSubmit,
        reset,
        formState: { isDirty },
    } = useForm();

    useEffect(() => {
        if (!isEditing && patient) {
            reset({
                email: patient.email,
                cellNumber: patient.cellNumber,
                phoneNumber: patient.phoneNumber,
                isActive: patient.isActive,
            });
        }
    }, [patient, isEditing, reset]);

    const handleEdit = () => {
        reset({
            email: patient.email,
            cellNumber: patient.cellNumber,
            phoneNumber: patient.phoneNumber,
            isActive: patient.isActive,
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        reset({
            email: patient.email,
            cellNumber: patient.cellNumber,
            phoneNumber: patient.phoneNumber,
            isActive: patient.isActive,
        });
        setIsEditing(false);
    };

    const onSubmit = async (data) => {
        try {
            const payload = {
                ...data,
                cellNumber: stripPhoneFormatting(data.cellNumber),
                phoneNumber: stripPhoneFormatting(data.phoneNumber),
            };

            const response = await patientService.updatePatientDetails(clinicId, patient.id, payload);
            onPatientUpdated?.(response.data);
            setIsEditing(false);
            showSuccess({ title: 'Patient Updated', text: 'Patient details were saved successfully.' });
        } catch (error) {
            console.error(error);
            showError({ title: 'Update Failed', text: 'Failed to update patient details.' });
        }
    };

    return (
        <div className="patient-info-module">
            <div className="patient-info-module__sections">
                <div className="patient-detail-tab-panel">
                    <div className="patient-detail-tab-panel__header">
                        <div>
                            <h3 className="patient-detail-tab-panel__title">
                                <PatientDetailIcon icon="bi-person-lines-fill" theme="indigo" size="sm" />
                                Patient Information
                            </h3>
                            <p className="patient-detail-tab-panel__subtitle">
                                Review profile details and update contact information.
                            </p>
                        </div>

                        {!isEditing ? (
                            <button type="button" className="btn btn-light-primary btn-sm" onClick={handleEdit}>
                                <i className="bi bi-pencil me-1" />
                                Edit
                            </button>
                        ) : (
                            <div className="d-flex gap-2">
                                <button type="button" className="ecx-modal__close-btn btn btn-sm btn-outline-secondary px-3" onClick={handleCancel}>
                                    <i className="bi bi-x-lg me-1" />
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-accent"
                                    disabled={!isDirty}
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    <i className="bi bi-check2 me-1" />
                                    Save
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="patient-detail-field-grid patient-detail-field-grid--three">
                        <DetailField label="First Name" value={patient?.firstName} />
                        <DetailField label="Last Name" value={patient?.lastName} />
                        <DetailField label="Status">
                            {isEditing ? (
                                <div className="patient-detail-field__value patient-detail-field__value--switch">
                                    <div className="form-check form-switch m-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="patient-active-switch"
                                            {...register('isActive')}
                                        />
                                        <label className="form-check-label" htmlFor="patient-active-switch">
                                            Active patient
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="patient-detail-field__value">
                                    <span
                                        className={`clinic-settings-status-pill ${
                                            patient?.isActive ? 'is-open' : 'is-closed'
                                        }`}
                                    >
                                        <i className={`bi ${patient?.isActive ? 'bi-check-circle' : 'bi-x-circle'}`} />
                                        {patient?.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            )}
                        </DetailField>
                    </div>

                    <div className="patient-detail-field-grid patient-detail-field-grid--three">
                        <DetailField label="Birth Date" value={patient?.dateOfBirth} />
                        <DetailField label="Age (years)" value={calculateAge(patient?.dateOfBirth)} />
                        <DetailField label="Gender" value={patient?.gender} />
                    </div>

                    <div className="patient-detail-field-grid patient-detail-field-grid--three">
                        <DetailField label="Email">
                            {isEditing ? (
                                <input type="email" className="form-control form-control-sm" {...register('email')} />
                            ) : (
                                <div className="patient-detail-field__value">{patient?.email || '—'}</div>
                            )}
                        </DetailField>

                        <DetailField label="Cell Number">
                            {isEditing ? (
                                <input type="tel" className="form-control form-control-sm" {...register('cellNumber')} />
                            ) : (
                                <div className="patient-detail-field__value">{patient?.cellNumber || '—'}</div>
                            )}
                        </DetailField>

                        <DetailField label="Phone Number">
                            {isEditing ? (
                                <input type="tel" className="form-control form-control-sm" {...register('phoneNumber')} />
                            ) : (
                                <div className="patient-detail-field__value">{patient?.phoneNumber || '—'}</div>
                            )}
                        </DetailField>
                    </div>
                </div>

                <PatientAppointments patient={patient} />
                <PatientActivity patientId={patient.id} />
                <PatientInvalidDetails patient={patient} />
            </div>
        </div>
    );
}

export default Overview;
